import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { useCallback } from "react";
import { z } from "zod";

import { PresentationPage } from "@/pages/presentation/presentation-page";
import { publicDecksBySlug } from "@generated/public-manifest";
import { loadDeckSheet } from "@lib/deck-content";
import { injectedDocMeta } from "@lib/injected-doc-meta";

import type { DeckMeta } from "@ming/shared";

function PresentationRoute() {
  const { deck, index, body } = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const onNavigate = useCallback(
    (sheet: number) => {
      void navigate({ search: (previous) => ({ ...previous, sheet }) });
    },
    [navigate],
  );

  return <PresentationPage deck={deck} index={index} body={body} onNavigate={onNavigate} />;
}

export const Route = createFileRoute("/p/$deck")({
  validateSearch: z.object({
    sheet: z.coerce.number().int().optional().catch(undefined),
  }),
  component: PresentationRoute,
  // The loader owns sheet resolution: out-of-range values are corrected once, right here.
  loaderDeps: ({ search: { sheet } }) => ({ sheet }),
  loader: async ({ params, deps: { sheet } }) => {
    // Listed decks come from the public manifest; an unlisted link opened directly gets its
    // metadata injected server-side by the worker instead of a public bundle.
    const deck = publicDecksBySlug.get(params.deck) ?? injectedDocMeta<DeckMeta>(params.deck);

    if (!deck) throw notFound();

    const requested = sheet ?? 1;
    const index = Math.min(Math.max(requested, 1), deck.sheets.length);

    if (index !== requested) {
      throw redirect({ to: "/p/$deck", params, search: { sheet: index }, replace: true });
    }

    return {
      deck,
      index,
      body: await loadDeckSheet(deck.slug, deck.sheets[index - 1].file),
    };
  },
});
